import * as PIXI from 'pixi.js-legacy';
import Room from './Room';
import constants from '../settings/constants';
import geometry from './util/geometry';
import emitter from '../emitter';
import { WALL_RESIZED } from '../settings/events';
import tacHelpers from '../state/tac/tacHelpers';
import DragDot from './boaxel/DragDot';
import DragLine from './boaxel/DragLine';
import store from '../state';
import { round } from '../util/round';
import { actionSetWallResizerInactive } from '../state/scene';
import { selectUserAgent } from '../state/userAgent/userAgentSelectors';
import { selectIsWallResizerActive } from '../state/scene/sceneSelectors';
import { selectTac } from '../state/tac/tacSelectors';

export default class FixedRoom extends Room {
  constructor(view, renderScene, stage) {
    super();

    this.stage = stage;
    this.renderScene = renderScene;

    this.dragDots = null;
    this.dragLines = null;

    this.view = view;
  }

  update(args) {
    super.update(args);
    const tac = selectTac(store.getState());
    const userAgent = selectUserAgent(store.getState());
    this.wallResizerActive = selectIsWallResizerActive(store.getState());
    this.model = this.getSpace(tac);

    this.ratio = this.getRatio(userAgent);

    this.floorHeight = this.slopeHeight = this.bottomDepth * this.ratio;
    this.slopeWidth = this.leftDepth * this.ratio;

    const wallPoints = tac?.wall?.points || constants.WALL.points;

    this.currentWall = this.getWallPxObject(wallPoints);
    this.maxWall = this.getWallPxObject();

    const itemContainerLimits = tac && tacHelpers.getWallResizingLimits(tac);

    this.draw({
      wallPoints,
      isMobile: this.isMobile,
      itemContainerLimits,
    });
  }

  getWallPxObject(pointsInMm) {
    const points = pointsInMm
      ? this.getWallPoints({ wallPoints: pointsInMm })
      : this.getMaxWallPoints();
    const { width, height } = geometry.surround(points);
    const position = this.getWallPosition({
      wallWidth: width,
      wallHeight: height,
    });

    return { points, width, height, position };
  }

  getSpace(tac) {
    return this.wallResizerActive
      ? tacHelpers.getMaxSpace()
      : tacHelpers.getCurrentSpace(tac);
  }

  getRatio(userAgent) {
    const { margins, wallResizerActive } = this;

    const horizontalRatio =
      this.targetWidth /
      (margins.right +
        (wallResizerActive
          ? constants.WALL.width.max + this.leftDepth
          : this.model.width + this.leftDepth));

    const verticalRatio =
      this.targetHeight /
      (this.margins.top +
        (wallResizerActive
          ? constants.WALL.height.max + this.bottomDepth
          : this.model.height + this.bottomDepth));

    return Math.min(horizontalRatio, verticalRatio);
  }

  getSkirtWidth() {
    return this.currentWall.width;
  }

  getSkirtY() {
    return super.getSkirtY();
  }

  getDefaultWallPoints() {
    if (constants.WALL) {
      return constants.WALL.points.map(point => ({
        x: point.x * this.ratio,
        y: point.y * this.ratio,
      }));
    }
    return super.getDefaultWallPoints();
  }

  /**
   * @param {object} args.wallPoints [{x, y}] in mm
   * @returns {object[]} [{x, y}] in px
   */
  getWallPoints({ wallPoints }) {
    if (this.wallResizerActive) {
      return this.getMaxWallPoints();
    } else if (wallPoints) {
      return wallPoints.map(point => ({
        x: (point.x > 0 ? point.x + this.margins.right : point.x) * this.ratio,
        y: (point.y > 0 ? point.y + this.margins.top : point.y) * this.ratio,
      }));
    } else {
      return this.getDefaultWallPoints();
    }
  }

  /**
   * @param {number} wallHeight wall height in px
   * @param {number} wallWidth wall width in px
   */
  getWallPosition({ wallHeight, wallWidth }) {
    return {
      y: round(this.targetHeight - wallHeight - this.floorHeight, 1),
      x: round(
        (this.targetWidth - wallWidth + this.leftDepth * this.ratio) / 2,
        1
      ),
    };
  }

  getWallResizerPosition(points, canvasContainerClientRect) {
    const { height } = geometry.surround(points);

    return {
      y:
        this.targetHeight -
        this.floorHeight -
        height +
        canvasContainerClientRect.top,
      x: this.maxWall.position.x + canvasContainerClientRect.left,
    };
  }

  draw(args) {
    super.draw(args);

    if (this.wallResizerActive) {
      this.drawSideWall();
      this.drawWallResizer(args.wallPoints, args.itemContainerLimits);
    } else {
      this.drawSideWall();
      this.dragDots && this.dragDots.forEach(dot => dot.destroy());
      this.dragDots = null;
      this.dragLines && this.dragLines.forEach(line => line.destroy());
      this.dragLines = null;
      this.backgroundSprite && this.backgroundSprite.destroy();
      this.backgroundSprite = null;
    }
  }

  getSkirtX() {
    return this.currentWall.position.x;
  }

  drawFloor() {
    const texture = PIXI.Loader.shared.resources.floor.texture;
    this.floor = this.floor || new PIXI.Sprite(texture);
    this.floorMask = this.floorMask || new PIXI.Graphics();

    const width = this.currentWall.width + this.slopeWidth;

    this.floor.width = width;
    this.floor.height = this.floorHeight;
    this.floor.x = this.currentWall.position.x - this.slopeWidth;
    this.floor.y = this.targetHeight - this.floor.height;
    this.floor.visible = true;

    this.floorMask.clear();
    this.floorMask.beginFill();
    const maskPoints = [
      new PIXI.Point(this.floor.x, this.floor.y + this.floorHeight),
      new PIXI.Point(this.floor.x + this.slopeWidth, this.floor.y),
      new PIXI.Point(this.floor.x + this.floor.width, this.floor.y),
      new PIXI.Point(
        this.floor.x + this.floor.width,
        this.floor.y + this.floorHeight
      ),
      new PIXI.Point(this.floor.x, this.floor.y + this.floorHeight),
    ];
    this.floorMask.drawPolygon(maskPoints);
    this.floorMask.endFill();

    this.addChild(this.floorMask);
    this.floor.mask = this.floorMask;

    this.addChild(this.floor);
  }

  drawSideWall() {
    // like in the flat wall we add lighting with a gradient, but change the angle
    this.sideWallBaseTexture =
      this.sideWallBaseTexture ||
      new PIXI.BaseTexture(
        this.createGradientCanvas({
          angle: constants.SIDEWALL_GRADIENT_ANGLE,
          lightColor: constants.SIDEWALL_GRADIENT_LIGHT_COLOR,
          darkColor: constants.SIDEWALL_GRADIENT_DARK_COLOR,
        })
      );

    this.sideWallTexture =
      this.sideWallTexture || new PIXI.Texture(this.sideWallBaseTexture);
    this.sideWallSprite =
      this.sideWallSprite || new PIXI.Sprite(this.sideWallTexture);
    this.sideWallMask = this.sideWallMask || new PIXI.Graphics();
    this.sideWallSkirt = this.sideWallSkirt || new PIXI.Graphics();
    this.sideWallSkirtEdge = this.sideWallSkirtEdge || new PIXI.Graphics();
    this.sideWallContainer = this.sideWallContainer || new PIXI.Container();

    const {
      currentWall,
      maxWall,
      sideWallContainer,
      sideWallBaseTexture,
      sideWallMask,
      sideWallSkirt,
      sideWallSkirtEdge,
      sideWallSprite,
      slopeHeight,
      slopeWidth,
    } = this;

    sideWallContainer.visible = true;

    sideWallBaseTexture.setSize(slopeWidth, maxWall.height + slopeHeight);

    sideWallSprite.position.set(
      currentWall.position.x - slopeWidth,
      maxWall.position.y
    );

    const maskPoints = [
      {
        x: 0,
        y: 0,
      },
      {
        x: slopeWidth,
        y: 0,
      },
      {
        x: slopeWidth,
        y: currentWall.height,
      },
      {
        x: 0,
        y: currentWall.height + slopeHeight,
      },
      {
        x: 0,
        y: 0,
      },
    ].map(point => {
      return new PIXI.Point(
        point.x + currentWall.position.x - slopeWidth,
        Math.max(point.y + currentWall.position.y, 0)
      );
    });

    sideWallMask.clear().beginFill().drawPolygon(maskPoints).endFill();
    sideWallSprite.mask = sideWallMask;

    // the skirting
    const skirtHeight = this.getSkirtHeight();
    const skirtPoints = [
      { x: 0, y: currentWall.height + slopeHeight - skirtHeight },
      { x: 0, y: currentWall.height + slopeHeight },
      { x: slopeWidth, y: currentWall.height },
      { x: slopeWidth, y: currentWall.height - skirtHeight },
      { x: 0, y: currentWall.height + slopeHeight - skirtHeight },
    ].map(point => {
      return new PIXI.Point(
        point.x + currentWall.position.x - slopeWidth,
        point.y + currentWall.position.y
      );
    });

    sideWallSkirt
      .clear()
      .beginFill(constants.SIDEWALL_SKIRT_COLOR)
      .drawPolygon(skirtPoints)
      .endFill();

    sideWallSkirtEdge
      .clear()
      .lineStyle(
        constants.SKIRT_EDGE_THICKNESS * this.ratio,
        constants.SIDEWALL_SKIRT_EDGE_COLOR
      )
      .moveTo(skirtPoints[1].x, skirtPoints[1].y - skirtHeight)
      .lineTo(skirtPoints[2].x, skirtPoints[2].y - skirtHeight);

    if (!sideWallContainer.parent) {
      sideWallContainer.addChild(
        sideWallMask,
        sideWallSprite,
        sideWallSkirt,
        sideWallSkirtEdge
      );
      this.addChild(sideWallContainer);
    }
  }

  drawWall() {
    this.gradientBaseTexture =
      this.gradientBaseTexture ||
      new PIXI.BaseTexture(
        this.createGradientCanvas({
          angle: constants.WALL_GRADIENT_ANGLE,
          lightColor: constants.GRADIENT_LIGHT_COLOR,
          darkColor: constants.GRADIENT_DARK_COLOR,
        })
      );

    this.wallTexture =
      this.wallTexture || new PIXI.Texture(this.gradientBaseTexture);
    this.wallSprite = this.wallSprite || new PIXI.Sprite(this.wallTexture);
    this.wallMask = this.wallMask || new PIXI.Graphics();
    this.wall = this.wall || new PIXI.Container();

    const {
      currentWall,
      floorHeight,
      gradientBaseTexture,
      maxWall,
      targetHeight,
      targetWidth,
      wall,
      wallMask,
      wallSprite,
    } = this;

    gradientBaseTexture.setSize(maxWall.width, maxWall.height);
    wallSprite.position.set(maxWall.position.x, maxWall.position.y);

    wallMask
      .clear()
      .beginFill()
      .drawRect(
        Math.max(currentWall.position.x, 0),
        Math.max(currentWall.position.y, 0),
        Math.min(currentWall.width, targetWidth - currentWall.position.x),
        Math.min(
          currentWall.height,
          this.wallResizerActive ? targetHeight : targetHeight - floorHeight
        )
      )
      .endFill();

    wallSprite.mask = wallMask;

    wall.x = currentWall.position.x;
    wall.y = currentWall.position.y;

    !wall.parent && this.addChild(wall);
    !wallMask.parent && this.addChild(wallMask);
    !wallSprite.parent && this.addChild(wallSprite);
  }

  getWallResizerPoints(wallPoints) {
    if (wallPoints) {
      return wallPoints.map(point => ({
        x: point.x * this.ratio,
        y: point.y * this.ratio,
      }));
    }

    return this.getDefaultWallPoints();
  }

  drawBackground(wallResizerWorldPoints) {
    // Find if an backgroundClickArea already exists
    // so we dont create multiple backgroundClickAreas
    // (draw() runs on every drag)
    const backgroundClickArea = this.stage.children.find(
      child => child.type && child.type === 'backgroundClickArea'
    );

    if (this.isMobile && this.isPortrait) {
      if (this.backgroundSprite) {
        this.backgroundSprite.pointerdown = null;
      }
      return;
    }

    if (!backgroundClickArea) {
      // Create the background area that will detect the click
      const canvas = this.view.getBoundingClientRect();
      const background = new PIXI.Graphics();
      background.drawRect(0, 0, canvas.width, canvas.height);
      const backgroundTexture = background.generateCanvasTexture();
      backgroundTexture.interactive = true;
      // For a PIXI element to be able to be interactive it can't be a Graphics
      this.backgroundSprite = new PIXI.Sprite(backgroundTexture);
      this.backgroundSprite.interactive = true;
      this.backgroundSprite.type = 'backgroundClickArea';

      this.stage.addChild(this.backgroundSprite);
    }

    this.backgroundSprite.pointerdown = e => {
      const polygon = [
        ...wallResizerWorldPoints.map(point => [point.x, point.y]),
      ];
      const point = [e.data.global.x, e.data.global.y];

      if (!geometry.isInsidePolygon(polygon, point)) {
        store.dispatch(actionSetWallResizerInactive({ nonInteraction: true }));
        this.backgroundSprite && this.backgroundSprite.destroy();
        this.backgroundSprite = null;
      }
    };
  }

  drawWallResizer(wallPoints, itemContainerLimits) {
    const points = this.getWallResizerPoints(wallPoints);

    const canvasContainerClientRect =
      this.view.parentElement.getBoundingClientRect();

    const wallResizerPosition = this.getWallResizerPosition(
      points,
      canvasContainerClientRect
    );

    const wallResizerWorldPoints = points.map(point => ({
      x: point.x + wallResizerPosition.x,
      y: point.y + wallResizerPosition.y,
    }));

    this.drawBackground(wallResizerWorldPoints);

    this.drawDragLines(points, itemContainerLimits, canvasContainerClientRect);

    if (this.isMobile) {
      this.dragDots && this.dragDots.forEach(dot => dot.destroy());
      this.dragDots = null;
    } else {
      this.drawDragDots(
        points,
        itemContainerLimits,
        canvasContainerClientRect,
        wallResizerPosition
      );
    }
    this.renderScene();

    emitter.emit(WALL_RESIZED, { points: wallResizerWorldPoints });
  }

  drawDragLines(points, itemContainerLimits, canvasContainerClientRect) {
    const position = this.getWallResizerPosition(
      points,
      canvasContainerClientRect
    );
    const dragLinePointsList = this.getDragLinePoints(points);
    if (!this.dragLines) {
      this.dragLines = [];

      for (let i = 0; i < dragLinePointsList.length; i++) {
        const dragLine = new DragLine({
          itemContainerLimits,
          prevWallSize: geometry.surround(points),
          canvasHeight: this.targetHeight,
          canvasContainer: canvasContainerClientRect,
          ratio: this.ratio,
          moveableAxes: this.getMoveableLineAxes(dragLinePointsList, i),
          points: [
            {
              x: dragLinePointsList[i][0].x + position.x,
              y: dragLinePointsList[i][0].y + position.y,
            },
            {
              x: dragLinePointsList[i][1].x + position.x,
              y: dragLinePointsList[i][1].y + position.y,
            },
          ],
          wallResizerOffset: {
            x: position.x,
            y: this.floorHeight,
          },
        });

        this.dragLines.push(dragLine);
        this.stage.addChild(dragLine.getDisplayObject());
      }
    } else {
      this.dragLines.forEach((line, i) => {
        line.update({
          points: [
            {
              x: dragLinePointsList[i][0].x + position.x,
              y: dragLinePointsList[i][0].y + position.y,
            },
            {
              x: dragLinePointsList[i][1].x + position.x,
              y: dragLinePointsList[i][1].y + position.y,
            },
          ],
          prevWallSize: geometry.surround(points),
        });
      });
    }
  }

  getDragLinePoints(points) {
    return points.map((point, i) => [point, points[(i + 1) % points.length]]);
  }

  drawDragDots(
    points,
    itemContainerLimits,
    canvasContainerClientRect,
    wallResizerPosition
  ) {
    let nonDraggablePointIndex = 0;
    for (let i = 1; i < points.length; i++) {
      if (
        points[i].y >= points[nonDraggablePointIndex].y &&
        points[i].x <= points[nonDraggablePointIndex].x
      ) {
        nonDraggablePointIndex = i;
      }
    }

    const dragPoints = points.slice();
    dragPoints.splice(nonDraggablePointIndex, 1);

    if (!this.dragDots) {
      this.dragDots = [];
      for (let i = 0; i < dragPoints.length; i++) {
        const dragDot = new DragDot({
          x: dragPoints[i].x + wallResizerPosition.x,
          y: dragPoints[i].y + wallResizerPosition.y,
          ratio: this.ratio,
          canvasHeight: this.targetHeight,
          canvasContainer: canvasContainerClientRect,
          moveableAxes: this.getMoveableDotAxes(dragPoints, i),
          prevWallSize: geometry.surround(points),
          itemContainerLimits,
          wallResizerOffset: {
            x: wallResizerPosition.x,
            y: this.floorHeight,
          },
        });
        this.dragDots.push(dragDot);

        this.stage.addChild(dragDot.getDisplayObject());
      }
    } else {
      this.dragDots.forEach((dot, i) => {
        dot.update({
          x: dragPoints[i].x + wallResizerPosition.x,
          y: dragPoints[i].y + wallResizerPosition.y,
          prevWallSize: geometry.surround(points),
        });
      });
    }
  }

  getMoveableLineAxes(dragLinePointsList, index) {
    if (
      dragLinePointsList.every(
        points =>
          dragLinePointsList[index][0].x >= points[0].x &&
          dragLinePointsList[index][1].x >= points[1].x
      )
    ) {
      return {
        x: true,
        y: false,
      };
    } else if (
      dragLinePointsList.every(
        points =>
          dragLinePointsList[index][0].y <= points[0].y &&
          dragLinePointsList[index][1].y <= points[1].y
      )
    ) {
      return {
        x: false,
        y: true,
      };
    }
    return {
      x: false,
      y: false,
    };
  }

  getMoveableDotAxes(points, index) {
    if (points.every(point => points[index].x <= point.x)) {
      return { x: false, y: true };
    } else if (
      points.every(point => points[index].x >= point.x) &&
      points.every(point => points[index].y <= point.y)
    ) {
      return { x: true, y: true };
    } else if (
      points.every(point => points[index].x >= point.x) &&
      points.every(point => points[index].y >= point.y)
    ) {
      return { x: true, y: false };
    }

    throw new Error('Unknown moveable Axes');
  }

  fillMobileWall(ctx, wallPointsInPx, position) {
    const { width: wallWidth, height: wallHeight } =
      geometry.surround(wallPointsInPx);

    const startingY = position.y < 0 ? -position.y : 0;
    const points = [
      { x: 0, y: startingY },
      { x: wallWidth, y: startingY },
      { x: wallWidth, y: wallHeight },
      { x: 0, y: wallHeight },
    ];

    ctx.beginPath();
    for (let i = 0; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y);
    }
    ctx.lineTo(points[0].x, points[0].y);
    ctx.closePath();
    ctx.fill();
  }

  fillWall(ctx, points, position) {
    ctx.beginPath();
    for (let i = 0; i < points.length; i++) {
      ctx.lineTo(points[i].x, points[i].y);
    }
    ctx.lineTo(points[0].x, points[0].y);
    ctx.closePath();
    ctx.fill();
  }

  getMaxWallPoints() {
    return [
      { x: 0, y: 0 },
      {
        x: (constants.WALL.width.max + this.margins.right) * this.ratio,
        y: 0,
      },
      {
        x: (constants.WALL.width.max + this.margins.right) * this.ratio,
        y: (constants.WALL.height.max + this.margins.top) * this.ratio,
      },
      {
        x: 0,
        y: (constants.WALL.height.max + this.margins.top) * this.ratio,
      },
    ];
  }
}
